// ItemFieldInput — item payload 필드 1개 입력 위젯 (등록 폼·수정 폼 공용).
//
// constants/itemFields.ts 의 FieldKind에 따라 위젯을 고른다:
//   text / secret(눈 토글) / multiline·secret-multiline(textarea) / number / date / select / list / boolean
// 브라우저에 저장된 Locker 로그인 비밀번호가 채워지지 않도록 autoComplete를 끈다
// (비밀 필드 new-password, 그 외 off) + 비밀번호 관리자 확장 무시 속성.

import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { isSecretKind, type ItemFieldMeta } from '../constants/itemFields';

interface ItemFieldInputProps {
  field: ItemFieldMeta;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  /** 같은 화면에 폼이 여러 개일 때 id 충돌 방지용 접두어 */
  idPrefix: string;
}

/** 자동완성·비밀번호 관리자 차단 속성 */
function noAutofill(secret: boolean, name: string) {
  return {
    name,
    autoComplete: secret ? 'new-password' : 'off',
    'data-lpignore': 'true',
    'data-1p-ignore': 'true',
  } as const;
}

export function ItemFieldInput({ field, value, onChange, error, idPrefix }: ItemFieldInputProps) {
  const [shown, setShown] = useState(false);
  const id = `${idPrefix}-${field.key}`;
  const secret = isSecretKind(field.kind);
  const label = `${field.label}${field.optional ? '' : ' *'}`;

  // 여러 줄 (비밀 여러 줄은 기본 가림 — CSS text-security, 눈 버튼으로 표시)
  if (field.kind === 'multiline' || field.kind === 'secret-multiline') {
    return (
      <div className="ui-input-wrap">
        <div className="item-field__label-row">
          <label className="ui-input-label" htmlFor={id}>{label}</label>
          {secret && (
            <button
              type="button"
              className={`inline-edit__eye-btn${shown ? ' inline-edit__eye-btn--on' : ''}`}
              onClick={() => setShown((s) => !s)}
              aria-label={shown ? `${field.label} 숨기기` : `${field.label} 보기`}
              aria-pressed={shown}
            >
              {shown ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          )}
        </div>
        <textarea
          id={id}
          className={`ui-input item-field__textarea${secret && !shown ? ' item-field__textarea--masked' : ''}${error ? ' ui-input--error' : ''}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          rows={field.kind === 'secret-multiline' ? 6 : 4}
          spellCheck={false}
          {...noAutofill(secret, `locker-${id}`)}
        />
        {error && <span className="ui-input-error" role="alert">{error}</span>}
      </div>
    );
  }

  // 선택지 / 예·아니오
  if (field.kind === 'select' || field.kind === 'boolean') {
    const options =
      field.kind === 'boolean'
        ? [{ value: 'true', label: '예' }, { value: 'false', label: '아니오' }]
        : field.options ?? [];
    return (
      <Select id={id} label={label} value={value} onChange={(e) => onChange(e.target.value)} error={error}>
        <option value="">{field.optional ? '선택 안 함' : '선택하세요'}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </Select>
    );
  }

  // 한 줄 입력 (text / secret / number / date / list)
  const inputType =
    field.kind === 'secret' ? (shown ? 'text' : 'password')
    : field.kind === 'number' ? 'number'
    : field.kind === 'date' ? 'date'
    : 'text';

  return (
    <Input
      id={id}
      label={label}
      type={inputType}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder}
      error={error}
      inputMode={field.kind === 'number' ? 'decimal' : undefined}
      {...noAutofill(secret, `locker-${id}`)}
      endAdornment={
        secret ? (
          <button
            type="button"
            className={`inline-edit__eye-btn${shown ? ' inline-edit__eye-btn--on' : ''}`}
            onClick={() => setShown((s) => !s)}
            aria-label={shown ? `${field.label} 숨기기` : `${field.label} 보기`}
            aria-pressed={shown}
          >
            {shown ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        ) : undefined
      }
    />
  );
}
